import { useState, useEffect } from 'react'
import { useDialog } from '../../context/DialogContext'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import { CheckCircle, Spinner, UserCheck } from '@phosphor-icons/react'
import { getPendingShippers, setShipperApproval, type PendingShipper } from '../../services/master-shippers'

export default function PendingShippersPage() {
  const { showError, showSuccess, confirm } = useDialog()
  const [shippers, setShippers] = useState<PendingShipper[]>([])
  const [loading, setLoading] = useState(true)
  const [actingId, setActingId] = useState<string | null>(null)

  async function load() {
    try {
      setLoading(true)
      setShippers(await getPendingShippers())
    } catch {
      showError('Erro', 'Erro ao carregar embarcadores pendentes')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  async function handleApprove(s: PendingShipper) {
    const ok = await confirm({
      title: 'Liberar embarcador',
      message: `Liberar "${s.name}" para criar cotações? Ele será notificado por e-mail.`,
      confirmText: 'Liberar',
    })
    if (!ok) return
    try {
      setActingId(s.shipper_id)
      await setShipperApproval(s.shipper_id, true)
      setShippers((prev) => prev.filter((x) => x.shipper_id !== s.shipper_id))
      showSuccess('Liberado', `${s.name} agora pode criar cotações.`)
    } catch {
      showError('Erro', 'Erro ao liberar embarcador')
    } finally {
      setActingId(null)
    }
  }

  const fmtDate = (d: string) => new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })

  return (
    <AppLayout>
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <UserCheck size={26} className="text-emerald-600" />
            Embarcadores pendentes
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Novos embarcadores aguardando liberação para criar cotações.
          </p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center py-16"><Spinner size={32} className="animate-spin text-emerald-600" /></div>
        ) : shippers.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center text-gray-500">
              <CheckCircle size={40} weight="fill" className="text-emerald-500 mx-auto mb-3" />
              Nenhum embarcador pendente. Tudo em dia!
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {shippers.map((s) => (
              <Card key={s.shipper_id}>
                <CardContent className="p-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{s.name}</p>
                    <p className="text-sm text-gray-500 truncate">
                      {s.document || '—'} · {s.email || 'sem e-mail'} · {s.phone || 'sem telefone'}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">Cadastrado em {fmtDate(s.created_at)}</p>
                  </div>
                  <Button
                    onClick={() => handleApprove(s)}
                    disabled={actingId === s.shipper_id}
                    className="gap-2 shrink-0"
                    style={{ backgroundColor: 'rgb(5, 150, 105)' }}
                  >
                    <CheckCircle size={18} weight="fill" />
                    {actingId === s.shipper_id ? 'Liberando...' : 'Liberar'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </AppLayout>
  )
}
